/* eslint-disable camelcase, no-undef */
import { handleAudioContributors, handleBookAutoAuthors } from './components/utils/component-utils';

/**
 * trackPageViews
 *
 * Track page views
 *
 * @return {Object} utag event
 */
const trackPageViews = () => {
  if ( typeof ( utag ) === 'undefined' ) return;

  const page_type = getPageType();
  const trackingObj = {
    page_name: document.title,
    page_type
  };

  return utag.view( trackingObj );
};

/**
 * trackNewsletterSubmit
 *
 * Track custom newsletter submit event
 *
 * @param {Object} signupResponse
 * @param {Object} data - form data or
 * @return {Object} utag event
 */
const trackNewsletterSubmit = ( signupResponse, data, isPreferencesPage = false ) => {
  if ( typeof ( utag ) === 'undefined' ) return;
  let pid = null;
  for ( const [ key, value ] of Object.entries( JSON.parse( signupResponse ) ) ) {
    if ( key === 'pid' ) {
      pid = value;
    }
  }

  const page_type = isPreferencesPage ? 'Newsletter Preferences' : getPageType();
  const newsletter_name = isPreferencesPage ? multipleNewsletters( data.subscriptions ) : singleNewsletter( data );
  const newsletter_acquisition_code = data.acquisitionCode;

  const trackingObj = {
    event_type: 'newsletter_signup',
    page_type,
    pid,
    newsletter_name,
    newsletter_acquisition_code
  };

  return utag.link( trackingObj );
};

/**
 * trackAudioPlay
 *
 * Build tracking object for utag.link
 *
 * @param {Object} data - form data
 * @param {boolean} isAutomatic - is entry automatic
 * @return {Object} utag event
 */
const trackAudioPlay = ( data, isAutomatic ) => {
  if ( typeof ( utag ) === 'undefined' ) return;
  const page_type = getPageType();
  const authors = handleAudioContributors( data, isAutomatic );

  const trackingObj = {
    event_type: 'audio_play',
    page_type,
    book_author: authors,
    book_title: data?.title,
    product_isbn: data?.isbn,
    product_imprint: data?.imprint,
    book_format: 'Audio',
    product_work_id: data?.workId,
    product_category: data?.categories,
    product_author_id: findAuthorId( data )
  };
  return utag.link( trackingObj );
};

/**
 * Tracks a book info click event.
 *
 * @param {Object} data - The book data.
 * @param {Object} context - The context object.
 * @param {string} context.entry - The context entry.
 * @param {string} context.module - The context module.
 * @returns {Object} The tracking object.
 */
const trackBookInfoClick = ( data, context ) => {
  const isAutomatic = context?.entry === 'automatic';
  const isAudioBook = data.format === 'Audio';
  const page_type = getPageType();
  let author = decodeURIComponent( data.author );
  const title = encodeURIComponent( data.title );
  author = isAudioBook ? handleAudioContributors( data, isAutomatic ) : author;
  author = isAutomatic ? handleBookAutoAuthors( data ) : author;

  const trackingObj = {
    page_name: document.title,
    event_type: 'view_book',
    product_view: 'quick_view',
    module_type: context.module,
    page_type,
    product_author: author,
    product_title: decodeURIComponent( title ),
    product_isbn: data.isbn,
    product_category: data?.categories,
    product_format: data?.format || data.frontlistiest?.format?.description,
    product_imprint: data?.imprint,
    product_division: data?.division,
    product_author_id: findAuthorId( data ),
    product_work_id: data?.workId
  };
  if ( typeof ( utag ) === 'undefined' ) {
    console.log( 'Calling utag.link with ', trackingObj );
    return true;
  } else {
    return utag.link( trackingObj );
  }
};

/**
 * Tracks a product share event and returns the result of the tracking call.
 * @param {Object} data - The data object containing information about the product being shared.
 * @param {string} name - The name of the social platform where the product is being shared.
 * @returns {Object} - The result of the tracking call.
 */
const trackShareClick = ( data, name, shareType ) => {
  if ( typeof ( utag ) === 'undefined' ) return;
  const page_type = getPageType();

  const trackingObj = {
    event_type: 'product_share',
    page_type,
    social_platform: name,
    share_type: shareType, // this is the only option currently see MPI-601 for analytics
    product_title: decodeURIComponent( title ),
    product_isbn: data?.isbn,
    product_imprint: data?.imprint,
    product_format: data?.format,
    product_work_id: data?.workId,
    product_category: data?.categories,
    product_author_id: findAuthorId( data )
  };
  return utag.link( trackingObj );
};

/**
 * findAuthorId
 *
 * Find authorId in data object
 *
 * @param {Object} data - form data
 * @return {string} authorId
 */

const findAuthorId = ( data ) => {
  let author = false;
  if ( Array.isArray( data.author ) ) {
    author = data.author.find( ( author ) => author.roleCodeDesc === 'Author' );
  }
  return author ? author.authorId : data?.authorId;
};

/**
 * singleNewsletter
 *
 * Process string for preferences
 *
 * @param {Object} obj - form data
 * @return {string} newsletter_name
 */
const singleNewsletter = ( data ) => {
  const preferenceId = data.preferenceId ? `|${data.preferenceId}` : '';
  const preferenceKey = data.preferenceKey ? `|${data.preferenceKey}` : '';
  const preferenceText = data.preferenceText ? `|${data.preferenceText}` : '';
  return `${data.siteId}|${data.programId}${preferenceId}${preferenceKey}${preferenceText}`;
};

/**
 * multipleNewsletters
 *
 * Merge strings
 *
 * @param {Object} obj - form data
 * @return {string} string of site, program preference ids, etc.
 */
const multipleNewsletters = ( data ) => {
  let string = '';
  // get the values of each key in data and combine it into a string
  data.forEach( ( item ) => {
    const programArray = [];
    programArray.push( item.siteId, item.programId );
    string += `${programArray.join( ',' ).replaceAll( ',', '|' )};`;
    if ( item.preferences ) {
      let preferenceArray = [];
      // cycle through the item.preferences object and return the values
      const preferences = Object.values( item.preferences );
      preferences.forEach( ( pref ) => {
        preferenceArray.push( item.siteId, item.programId );
        preferenceArray.push( pref.preferenceId, pref.preferenceText );
        string += `${preferenceArray.join( ',' ).replaceAll( ',', '|' )};`;
        // reset preferenceArray
        preferenceArray = [];
      });
    }
  });
  return string;
};

/**
 * getPageType
 *
 * Check if the page-preferences class exists;
 *
 * @return {string} home|page|bookcategory|imprint|post
 */
const getPageType = () => {
  const body = document.body;
  const isHomePage = document.body.classList.contains( 'home' );
  const arr = [ 'page', 'single-bookcategory', 'single-imprint', 'single-post' ];
  const pageClassName = arr.map( ( item ) => body.classList.contains( item ) ? item : null ).filter( ( filteredItem ) => filteredItem !== null ).toString();
  const pageType = pageClassName.includes( '-' ) ? pageClassName.split( '-' )[1].toString() : pageClassName;
  return isHomePage ? 'home' : pageType;
};

export {
  trackAudioPlay, trackBookInfoClick, trackNewsletterSubmit, trackPageViews, trackShareClick
};
